import type {
  FetchDocumentTypesFn,
  PrimeDocumentTypeByShortIdFn,
} from '@/features/documents'
import type {
  GetCurrentUserWorkspaceMembershipsFn,
  PrimeUserFn,
  PrimeWorkspaceFn,
} from '@/features/iam'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import type { Id } from '@/infra/id'
import { createInternalFeatureLoader } from '@/utils/feature-loading-util'
import type { CursorPaged } from '@/infra/paging'
import type { Observable } from 'rxjs'
import type { InventoryItem } from './InventoryItem'
import type {
  ImportedInventoryItem,
  InventoryItemExport,
} from './inventory-item-export'
import type { InventoryItemForProvider } from './InventoryItemForProvider'
import type {
  ListInventoryItemsForProviderQuery,
  ListInventoryItemsQuery,
} from './InventoryStore'

/**
 * Options for {@link InventoryFeature}.
 */
export interface InventoryFeatureOptions {
  token$: Observable<string>
  newAlert: AlertFactory
  createAlertErrorHandler: AlertErrorHandlerFactory
  createAlertRetryWrapper: AlertRetryWrapperFactory
  primeWorkspace: PrimeWorkspaceFn
  primeUser: PrimeUserFn
  getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn
  primeDocumentTypeByShortId: PromiseLike<PrimeDocumentTypeByShortIdFn>
  fetchDocumentTypes: FetchDocumentTypesFn
}

/**
 * Lists the service items of the given workspaces, or the global inventory
 * when no workspace is given.
 */
export type ListInventoryItemsFn = (
  query: ListInventoryItemsQuery,
  signal?: AbortSignal,
) => Promise<CursorPaged<InventoryItem>>

/**
 * Lists the service items a pro may add to a job in the workspace.
 */
export type ListInventoryItemsForProviderFn = (
  query: ListInventoryItemsForProviderQuery,
  signal?: AbortSignal,
) => Promise<CursorPaged<InventoryItemForProvider>>

/**
 * Fetches the workspace's service items with the given IDs.
 */
export type FetchInventoryItemsByIdsFn = (
  ids: Id[],
  workspaceId: Id,
  signal?: AbortSignal,
) => Promise<InventoryItem[]>

/**
 * Fetches the workspace's service items with the given IDs as exports, which
 * keep every field so that they can be imported into another workspace.
 */
export type FetchInventoryItemExportsFn = (
  ids: Id[],
  workspaceId: Id,
  signal?: AbortSignal,
) => Promise<InventoryItemExport[]>

/**
 * Imports exported service items into the workspace.
 */
export type ImportInventoryItemsFn = (
  items: InventoryItemExport[],
  workspaceId: Id,
) => Promise<ImportedInventoryItem[]>

/**
 * Features relating to the service item inventory.
 */
export type InventoryFeature = ReturnType<typeof InventoryFeature>

export function InventoryFeature(options: InventoryFeatureOptions) {
  const internal = createInternalFeatureLoader(
    () => import('./internal'),
    // The store primes document types while mapping, so the lazy documents
    // feature is loaded before it.
    async (m) =>
      m.InternalInventoryFeature(
        options,
        await options.primeDocumentTypeByShortId,
      ),
  )

  const listInventoryItems: ListInventoryItemsFn = internal.wrap(
    (m) => m.listInventoryItems,
  )

  const listInventoryItemsForProvider: ListInventoryItemsForProviderFn =
    internal.wrap((m) => m.listInventoryItemsForProvider)

  const fetchInventoryItemsByIds: FetchInventoryItemsByIdsFn = internal.wrap(
    (m) => m.fetchInventoryItemsByIds,
  )

  const fetchInventoryItemExports: FetchInventoryItemExportsFn = internal.wrap(
    (m) => m.fetchInventoryItemExports,
  )

  const importInventoryItems: ImportInventoryItemsFn = internal.wrap(
    (m) => m.importInventoryItems,
  )

  return {
    // Internal
    internal,

    // Public
    listInventoryItems,
    listInventoryItemsForProvider,
    fetchInventoryItemsByIds,
    fetchInventoryItemExports,
    importInventoryItems,
  }
}
